import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Flex,
  Icon,
  Text,
  useColorModeValue,
  Stack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Spinner,
  FormControl,
  FormLabel,
  Input,
  IconButton,
} from "@chakra-ui/react";
import { MdAdd, MdClose, MdCardGiftcard, MdRemove } from "react-icons/md";
import Card from "components/card/Card";
import { Pagination } from "antd";

export default function RewardPlanRules({
  allPlans, // Current page of plans from API
  totalPlans, // Total count for pagination
  currentPage, // Current page number
  onPageChange, // Handler for page change
  selectedPlans, // Array of selected full plan objects
  setSelectedPlans, // Setter for selected plans
  loading, // Loading state for API fetch
  rewardRatio, // Global reward ratio
  setRewardRatio, // Setter for ratio
  onSave, // Save handler
  isSaveDisabled, // Whether save is disabled
}) {
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const secondaryText = useColorModeValue("secondaryGray.600", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.200");
  const modalHoverBg = useColorModeValue("gray.50", "whiteAlpha.100");

  // Custom Color as requested
  const accentColor = "#1677ff";
  const accentAlpha = "rgba(9, 129, 200, 0.1)"; // Light blue for selection background

  const [isModalOpen, setIsModalOpen] = useState(false);

  // Local selection state for the modal (IDs only)
  const [tempSelectedIds, setTempSelectedIds] = useState(new Set());

  // Whenever modal opens, reset temp state to match actual selected plans
  useEffect(() => {
    if (isModalOpen) {
      const ids = new Set(selectedPlans.map((p) => p.id));
      setTempSelectedIds(ids);
    }
  }, [isModalOpen, selectedPlans]);

  const handleTogglePlan = (plan) => {
    const newIds = new Set(tempSelectedIds);
    if (newIds.has(plan.id)) {
      newIds.delete(plan.id);
    } else {
      newIds.add(plan.id);
    }
    setTempSelectedIds(newIds);
  };

  const handleConfirmSelection = () => {
    const keptPlans = selectedPlans.filter((p) => tempSelectedIds.has(p.id));
    const newPlansFromCurrentPage = allPlans.filter(
      (p) =>
        tempSelectedIds.has(p.id) &&
        !keptPlans.find((existing) => existing.id === p.id)
    );
    setSelectedPlans([...keptPlans, ...newPlansFromCurrentPage]);
    setIsModalOpen(false);
  };

  const handleRemovePlan = (planId) => {
    setSelectedPlans(selectedPlans.filter((p) => p.id !== planId));
  };

  const handleIncrement = () => {
    setRewardRatio(parseFloat((rewardRatio + 0.1).toFixed(1)));
  };

  const handleDecrement = () => {
    setRewardRatio(Math.max(0, parseFloat((rewardRatio - 0.1).toFixed(1))));
  };

  const handleChangeRatio = (e) => {
    const val = e.target.value;
    if (val === "") {
      setRewardRatio("");
      return;
    }
    const parsed = parseFloat(val);
    if (!isNaN(parsed) && parsed >= 0) {
      setRewardRatio(val);
    }
  };

  const handleBlurRatio = () => {
    if (rewardRatio === "" || parseFloat(rewardRatio) === 0) {
      setRewardRatio(1);
    } else {
      setRewardRatio(parseFloat(parseFloat(rewardRatio).toFixed(1)));
    }
  };

  return (
    <>
      <Card mb="20px">
        <Flex align="center" justify="space-between" mb="20px">
          <Text color={accentColor} fontSize="22px" fontWeight="700">
            Plan Rules
          </Text>
          <Button
            bg={accentColor}
            color="white"
            _hover={{ bg: "#0870ad" }}
            _active={{ bg: "#076094" }}
            fontSize="sm"
            fontWeight="500"
            leftIcon={<Icon as={MdAdd} color="white" />}
            onClick={() => setIsModalOpen(true)}
          >
            Add Rule
          </Button>
        </Flex>

        {/* Selected Plans List (Compact) */}
        <Box mb="24px">
          {selectedPlans.length > 0 ? (
            <Stack spacing="8px">
              {selectedPlans.map((plan) => (
                <Flex
                  key={plan.id}
                  p="10px 16px"
                  border="1px solid"
                  borderColor={borderColor}
                  borderRadius="8px"
                  justify="space-between"
                  align="center"
                  bg="transparent"
                >
                  <Box>
                    <Text color={textColor} fontSize="sm" fontWeight="700">
                      {plan.name}
                    </Text>
                    <Text color={secondaryText} fontSize="xs">
                      {plan.member_count || 0} members • $ {plan.cost}
                    </Text>
                  </Box>
                  <IconButton
                    size="xs"
                    colorScheme="red"
                    variant="ghost"
                    icon={<Icon as={MdClose} />}
                    onClick={() => handleRemovePlan(plan.id)}
                    aria-label="Remove plan"
                  />
                </Flex>
              ))}
            </Stack>
          ) : (
            <Flex
              direction="column"
              align="center"
              justify="center"
              py="20px"
              textAlign="center"
              bg="gray.50"
              borderRadius="8px"
              border="1px dashed"
              borderColor="gray.300"
            >
              <Icon
                as={MdCardGiftcard}
                width="30px"
                height="30px"
                color="gray.400"
                mb="8px"
              />
              <Text color="gray.500" fontSize="sm">
                No plans selected
              </Text>
            </Flex>
          )}
        </Box>

        {/* Reward Ratio Input Section */}
        <Box pt="20px" borderTop="1px solid" borderColor={borderColor}>
          <FormControl>
            <FormLabel color={textColor} fontSize="sm" fontWeight="500">
              Global Reward Ratio (Points per dollar spent)
            </FormLabel>
            <Flex align="center" gap="12px" mb="20px">
              <IconButton
                icon={<MdRemove />}
                onClick={handleDecrement}
                aria-label="Decrease ratio"
                size="sm"
                bg={accentColor}
                color="white"
                _hover={{ bg: "#0870ad" }}
              />
              <Input
                type="number"
                value={rewardRatio}
                onChange={handleChangeRatio}
                onBlur={handleBlurRatio}
                step="0.1"
                min="0"
                width="100px"
                textAlign="center"
                fontWeight="700"
                size="md"
                borderColor={borderColor}
                _focus={{ borderColor: accentColor }}
              />
              <IconButton
                icon={<MdAdd />}
                onClick={handleIncrement}
                aria-label="Increase ratio"
                size="sm"
                bg={accentColor}
                color="white"
                _hover={{ bg: "#0870ad" }}
              />
            </Flex>
          </FormControl>
        </Box>

        {/* Selection Modal */}
        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          size="lg"
        >
          <ModalOverlay />
          <ModalContent maxH="80vh">
            <ModalHeader color={accentColor}>
              Select Membership Plans
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody overflowY="auto" maxH="60vh">
              {loading ? (
                <Flex justify="center" py={10}>
                  <Spinner color={accentColor} />
                </Flex>
              ) : (
                <Stack spacing={2}>
                  {allPlans.map((plan) => {
                    const isSelected = tempSelectedIds.has(plan.id);
                    return (
                      <Flex
                        key={plan.id}
                        p="12px"
                        borderWidth="1px"
                        borderColor={isSelected ? accentColor : borderColor}
                        borderRadius="md"
                        align="center"
                        justify="space-between"
                        bg={isSelected ? accentAlpha : "transparent"}
                        _hover={{ bg: isSelected ? accentAlpha : modalHoverBg }}
                        cursor="pointer"
                        onClick={() => handleTogglePlan(plan)}
                        transition="all 0.2s"
                      >
                        <Box>
                          <Text
                            fontWeight={isSelected ? "700" : "500"}
                            color={isSelected ? accentColor : textColor}
                          >
                            {plan.name}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            {plan.member_count || 0} members • $ {plan.cost}
                          </Text>
                        </Box>
                        {isSelected && (
                          <Icon as={MdCardGiftcard} color={accentColor} />
                        )}
                      </Flex>
                    );
                  })}
                </Stack>
              )}

              <Flex justify="center" mt={6} pb={2}>
                <Pagination
                  current={currentPage}
                  total={totalPlans}
                  pageSize={10}
                  onChange={onPageChange}
                  size="small"
                  showSizeChanger={false}
                />
              </Flex>
            </ModalBody>

            <ModalFooter>
              <Button
                variant="ghost"
                mr={3}
                onClick={() => setIsModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                bg={accentColor}
                color="white"
                _hover={{ bg: "#0870ad" }}
                onClick={handleConfirmSelection}
              >
                Confirm Selection
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Card>
    </>
  );
}
